import { useState, useEffect, useRef } from 'react'
import { Button, Input, Slider } from 'components'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import { useRecoilState, useSetRecoilState } from 'recoil'
import {
  editBackgroundImageAtom,
  pageThemeOverrideSelector,
  showPinturaAtom,
} from 'atoms'
import { useSetToast, useUpdateThemeOverrides } from 'hooks'
import { ToggleSwitch } from 'components/global/ToggleSwitch/ToggleSwitch'
import { useTranslation } from 'react-i18next'
import { parseBackgroundFill } from '@/utils/editor/backgroundUtils'

export const BackgroundSettingsDrawer = () => {
  const { t } = useTranslation()
  const updateThemeOverrides = useUpdateThemeOverrides()
  const setShowPintura = useSetRecoilState(showPinturaAtom)
  const setEditBackgroundImage = useSetRecoilState(editBackgroundImageAtom)

  const [hideBackground, setHideBackground] = useRecoilState(
    pageThemeOverrideSelector({ path: 'hide_background' }),
  )
  const [backgroundY, setBackgroundY] = useRecoilState(
    pageThemeOverrideSelector({ path: 'background_top' }),
  )
  const [backgroundX, setBackgroundX] = useRecoilState(
    pageThemeOverrideSelector({ path: 'background_left' }),
  )
  const [backgroundSize, setBackgroundSize] = useRecoilState(
    pageThemeOverrideSelector({ path: 'background_fill' }),
  )

  const [backgroundOpacity, setBackgroundOpacity] = useRecoilState(
    pageThemeOverrideSelector({ path: 'background_opacity' }),
  )
  const [backgroundWidth, setBackgroundWidth] = useState(() => {
    const { backgroundWidthValue } = parseBackgroundFill(backgroundSize)
    return backgroundWidthValue
  })

  const [backgroundHeight, setBackgroundHeight] = useState(() => {
    const { backgroundHeightValue } = parseBackgroundFill(backgroundSize)
    return backgroundHeightValue
  })

  const setToast = useSetToast()

  const setComingSoon = () => setToast({ text: t('toast.comingSoon') })

  const applyChanges = (selectedPages) => {
    updateThemeOverrides(selectedPages, {
      background_top: backgroundY,
      background_left: backgroundX,
      background_fill: backgroundSize,
      background_opacity: backgroundOpacity / 100,
    })
  }

  useEffect(() => {
    // Keep the local state in sync when the raw background fill value changes
    const parsedBackgroundFill = parseBackgroundFill(backgroundSize)
    setBackgroundWidth(parsedBackgroundFill.backgroundWidthValue)
    setBackgroundHeight(parsedBackgroundFill.backgroundHeightValue)
  }, [backgroundSize])

  const isInitialMountRef = useRef(true)

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      return
    }

    setBackgroundSize(
      `${backgroundWidth !== 'auto' ? `${backgroundWidth}%` : 'auto'} ${
        backgroundHeight !== 'auto' ? `${backgroundHeight}%` : 'auto'
      }`,
    )
    //eslint-disable-next-line
  }, [backgroundWidth, backgroundHeight])

  useEffect(() => {
    setEditBackgroundImage(true)

    return () => setEditBackgroundImage(false)
    // eslint-disable-next-line
  }, [])

  return (
    <Flex column padding="0 12px 0 16px">
      <Text margin="0 0 16px">
        {t('editor.menus.properties.backgroundSettingsDrawer.positionText')}
      </Text>
      <Flex margin="0 0 16px">
        <Input
          value={[backgroundX, setBackgroundX]}
          label={t(
            'editor.menus.properties.backgroundSettingsDrawer.leftLabel',
          )}
          margin="0 16px 0 0"
          placeholder="100"
          type="number"
        />
        <Input
          value={[backgroundY, setBackgroundY]}
          label={t('editor.menus.properties.backgroundSettingsDrawer.topLabel')}
          margin="0 16px 0 0"
          placeholder="100"
          type="number"
        />
      </Flex>
      <Text margin="0 0 16px">
        {t('editor.menus.properties.backgroundSettingsDrawer.sizeText')}
      </Text>
      <Flex margin="0 0 16px">
        {backgroundWidth !== 'auto' && (
          <>
            <Input
              value={[backgroundWidth, setBackgroundWidth]}
              label={t(
                'editor.menus.properties.backgroundSettingsDrawer.widthFillLabel',
              )}
              margin="0 16px 0 0"
              placeholder="100"
              type="number"
            />
          </>
        )}
      </Flex>
      {backgroundWidth !== 'auto' && (
        <Flex full column align="flex-start" margin="0 0 20px">
          <Text size="12px" weight="500">
            {t(
              'editor.menus.properties.backgroundSettingsDrawer.widthFillLabel',
            )}
          </Text>
          <Slider
            sliderState={[backgroundWidth, setBackgroundWidth]}
            min="0"
            max="400"
            initialValue={100}
          />
        </Flex>
      )}
      {backgroundHeight !== 'auto' && (
        <Flex full column align="flex-start" margin="0 0 20px">
          <Text size="12px" weight="500">
            {t(
              'editor.menus.properties.backgroundSettingsDrawer.heightFillLabel',
            )}
          </Text>
          <Slider
            sliderState={[backgroundHeight, setBackgroundHeight]}
            min="0"
            max="400"
            initialValue={100}
          />
        </Flex>
      )}
      <Flex full column align="flex-start" margin="0 0 20px">
        <Text size="12px" weight="500" margin="0 24px 0 0">
          {t('editor.menus.properties.backgroundSettingsDrawer.opacityText')}
        </Text>
        <Slider
          sliderState={[
            !isNaN(backgroundOpacity) ? backgroundOpacity : 0,
            setBackgroundOpacity,
          ]}
          min="0"
          max="100"
          initialValue={0}
        />
      </Flex>
      <Flex margin="0 0 16px">
        <ToggleSwitch
          label={t(
            'editor.menus.properties.backgroundSettingsDrawer.hideBackgroundLabel',
          )}
          value={[hideBackground, setHideBackground]}
          labelLeft
        />
      </Flex>
      <Button margin="0 0 16px" onClick={() => setShowPintura(true)} center>
        {t('editor.menus.properties.backgroundSettingsDrawer.editImageButton')}
      </Button>
      <Button margin="0 0 16px" onClick={() => setComingSoon()}>
        {t(
          'editor.menus.properties.backgroundSettingsDrawer.revertImageButton',
        )}
      </Button>
      <Button margin="0 0 16px" onClick={() => applyChanges('even')}>
        {t(
          'editor.menus.properties.backgroundSettingsDrawer.applyToEvenPagesButton',
        )}
      </Button>
      <Button margin="0 0 16px" onClick={() => applyChanges('odd')}>
        {t(
          'editor.menus.properties.backgroundSettingsDrawer.applyToOddPagesButton',
        )}
      </Button>
      <Button margin="0 0 16px" onClick={() => applyChanges('all')}>
        {t(
          'editor.menus.properties.backgroundSettingsDrawer.applyToAllPagesButton',
        )}
      </Button>
    </Flex>
  )
}

export default BackgroundSettingsDrawer
